import React, { useState } from 'react';
import { Reveal } from './Reveal';

/**
 * Intent launcher — the "what do you want to do?" band.
 *
 * A row of large, verb-led action cards for the top of an overview screen.
 * Each card is a doorway to a task (create / manage / configure). Designed to
 * feel inviting and tactile: gradient wash, animated glow on hover, a sliding
 * arrow, and a subtle lift. Fully keyboard-accessible (native <button>).
 */

export interface IntentAction {
  key: string;
  label: string;       // the verb phrase, e.g. "Create organization"
  description: string; // one line of helper text
  icon: string;        // emoji
  /** Accent gradient [from, to] for this card. */
  gradient: [string, string];
  onClick: () => void;
  /** Optional: render as the primary/featured card (larger, filled). */
  featured?: boolean;
}

export function IntentLauncher({
  title = 'What would you like to do?',
  actions,
}: {
  title?: string;
  actions: IntentAction[];
}) {
  return (
    <section style={styles.wrap} aria-label="Quick actions">
      <div style={styles.title}>{title}</div>
      <div style={styles.grid}>
        {actions.map((a, i) => (
          <Reveal key={a.key} index={i} style={{ display: 'flex' }}>
            <IntentCard action={a} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function IntentCard({ action }: { action: IntentAction }) {
  const [hover, setHover] = useState(false);
  const [from, to] = action.gradient;

  return (
    <button
      type="button"
      onClick={action.onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      aria-label={action.label}
      style={{
        ...styles.card,
        ...(action.featured ? styles.cardFeatured : null),
        transform: hover ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hover
          ? `0 16px 40px -12px ${from}66, 0 4px 12px rgba(16,24,40,0.08)`
          : '0 1px 3px rgba(16,24,40,0.06)',
        borderColor: hover ? `${from}55` : '#e8eaf0',
      }}
    >
      {/* Animated gradient glow blob */}
      <span
        aria-hidden="true"
        style={{
          ...styles.glow,
          background: `radial-gradient(circle at 30% 30%, ${from}, ${to})`,
          opacity: hover ? 0.28 : 0.14,
          transform: hover ? 'scale(1.25)' : 'scale(1)',
        }}
      />
      <span
        style={{
          ...styles.iconWrap,
          background: `linear-gradient(135deg, ${from}, ${to})`,
          transform: hover ? 'rotate(-6deg) scale(1.06)' : 'rotate(0) scale(1)',
        }}
      >
        <span style={styles.icon}>{action.icon}</span>
      </span>
      <span style={styles.body}>
        <span style={styles.label}>{action.label}</span>
        <span style={styles.desc}>{action.description}</span>
      </span>
      <span
        aria-hidden="true"
        style={{ ...styles.arrow, transform: hover ? 'translateX(4px)' : 'translateX(0)', color: from }}
      >
        →
      </span>
    </button>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { marginBottom: 28 },
  title: { fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8a93a6', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 },
  card: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    textAlign: 'left',
    padding: '18px 18px',
    borderRadius: 18,
    border: '1px solid #e8eaf0',
    background: '#fff',
    cursor: 'pointer',
    width: '100%',
    transition: 'transform 0.18s cubic-bezier(.2,.8,.2,1), box-shadow 0.18s ease, border-color 0.18s ease',
  },
  cardFeatured: { background: 'linear-gradient(135deg, #ffffff, #fbfbff)' },
  glow: {
    position: 'absolute',
    top: -40,
    right: -30,
    width: 150,
    height: 150,
    borderRadius: '50%',
    filter: 'blur(8px)',
    transition: 'opacity 0.25s ease, transform 0.35s cubic-bezier(.2,.8,.2,1)',
    pointerEvents: 'none',
  },
  iconWrap: {
    flexShrink: 0,
    width: 48,
    height: 48,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    boxShadow: '0 6px 16px -6px rgba(16,24,40,0.35)',
    transition: 'transform 0.25s cubic-bezier(.2,.8,.2,1)',
    zIndex: 1,
  },
  icon: { fontSize: '1.4rem', lineHeight: 1, filter: 'saturate(1.1)' },
  body: { display: 'flex', flexDirection: 'column', gap: 2, zIndex: 1, minWidth: 0 },
  label: { fontWeight: 800, fontSize: '0.98rem', color: '#0b1120', letterSpacing: '-0.01em' },
  desc: { fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.35 },
  arrow: { marginLeft: 'auto', fontSize: '1.2rem', fontWeight: 700, transition: 'transform 0.2s ease', zIndex: 1 },
};
